import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import { ArrowLeft, Loader2, MapPin, Monitor, Calendar, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
}

interface ScanEvent {
  id: string;
  profile_id: string;
  scanned_at: string;
  location: string | null;
  user_agent: string | null;
  ip_address: string | null;
}

const Statistics = () => {
  const navigate = useNavigate();
  const { isPremium, isPro, loading: subLoading } = useSubscription();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>("all");
  const [scans, setScans] = useState<ScanEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalScans, setTotalScans] = useState(0);

  useEffect(() => {
    checkAccessAndLoadData();
  }, [isPremium, isPro, subLoading]);

  useEffect(() => {
    if (profiles.length > 0) {
      loadScans();
    }
  }, [selectedProfileId, profiles]);

  const checkAccessAndLoadData = async () => {
    // Check authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast.error("Bitte melden Sie sich an");
      navigate("/auth");
      return;
    }

    // Check if user has premium or pro plan
    if (!subLoading && !isPremium && !isPro) {
      toast.error("Diese Funktion ist nur für Premium und Pro Mitglieder verfügbar");
      navigate("/pricing");
      return;
    }

    await loadProfiles(session.user.id);
  };

  const loadProfiles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .eq("user_id", userId);

      if (error) throw error;
      setProfiles(data || []);
    } catch (error) {
      console.error("Error loading profiles:", error);
      toast.error("Fehler beim Laden der Profile");
    }
  };

  const loadScans = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("profile_scans")
        .select("*")
        .order("scanned_at", { ascending: false });

      // Filter by selected profile if not "all"
      if (selectedProfileId !== "all") {
        query = query.eq("profile_id", selectedProfileId);
      } else if (profiles.length > 0) {
        // Filter by all user's profiles
        const profileIds = profiles.map(p => p.id);
        query = query.in("profile_id", profileIds);
      }

      const { data, error, count } = await query;

      if (error) throw error;
      setScans(data || []);
      setTotalScans(data?.length || 0);
    } catch (error) {
      console.error("Error loading scans:", error);
      toast.error("Fehler beim Laden der Scan-Statistiken");
    } finally {
      setLoading(false);
    }
  };

  const getProfileName = (profileId: string) => {
    const profile = profiles.find(p => p.id === profileId);
    return profile ? `${profile.first_name} ${profile.last_name}` : "Unbekannt";
  };

  if (subLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zum Dashboard
          </Button>
        </Link>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            Scan <span className="gradient-text">Statistiken</span>
          </h1>
          <p className="text-muted-foreground">
            Sehen Sie, wann und wie oft Ihre Notfallprofile gescannt wurden
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gesamt Scans</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalScans}</div>
              <p className="text-xs text-muted-foreground">
                Alle Profile zusammen
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Profile</CardTitle>
              <Monitor className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{profiles.length}</div>
              <p className="text-xs text-muted-foreground">
                Aktive Notfallprofile
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Letzter Scan</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {scans.length > 0 
                  ? new Date(scans[0].scanned_at).toLocaleDateString("de-DE")
                  : "Noch keine"
                }
              </div>
              <p className="text-xs text-muted-foreground">
                {scans.length > 0 
                  ? new Date(scans[0].scanned_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
                  : "Scans vorhanden"
                }
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filter</CardTitle>
            <CardDescription>Wählen Sie ein Profil zur Ansicht</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
              <SelectTrigger className="w-full md:w-[300px]">
                <SelectValue placeholder="Profil auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Profile</SelectItem>
                {profiles.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.first_name} {profile.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Scan Events List */}
        <Card>
          <CardHeader>
            <CardTitle>Scan-Ereignisse</CardTitle>
            <CardDescription>
              {scans.length === 0 
                ? "Noch keine Scans aufgezeichnet" 
                : `${scans.length} Scan${scans.length === 1 ? "" : "s"} gefunden`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {scans.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Monitor className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Ihre QR-Codes wurden noch nicht gescannt</p>
                <p className="text-sm mt-2">Scan-Ereignisse werden hier automatisch angezeigt</p>
              </div>
            ) : (
              <div className="space-y-4">
                {scans.map((scan) => (
                  <div
                    key={scan.id}
                    className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {getProfileName(scan.profile_id)}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {new Date(scan.scanned_at).toLocaleDateString("de-DE", {
                            dateStyle: "full",
                          })} um {new Date(scan.scanned_at).toLocaleTimeString("de-DE", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {scan.location && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>{scan.location}</span>
                        </div>
                      )}
                      {scan.user_agent && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Monitor className="h-4 w-4" />
                          <span className="truncate" title={scan.user_agent}>
                            {scan.user_agent.substring(0, 50)}
                            {scan.user_agent.length > 50 ? "..." : ""}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Statistics;
